import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Button,
  IconButton,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  People as PeopleIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Settings as ManageIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import UserCreateDialog from '../../components/admin/UserCreateDialog';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { LoadingScreen } from '../../components/common/LoadingScreen';
import { useUserRoles } from '../../hooks/useUserRoles';

interface User {
  uuid: string;
  name: string;
  email: string;
  type: string;
  api_access_enabled: boolean;
  created_at: string;
  roles: string[];
}

export default function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const navigate = useNavigate();
  const { isAdmin, userUuid } = useUserRoles();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const usersData = await apiClient.get<User[]>('/admin/users');
      setUsers(usersData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`/admin/users/${deleteTarget.uuid}`);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  // Filter by search query then separate by type
  const query = searchQuery.toLowerCase();
  const filteredUsers = query
    ? users.filter(user => user.name.toLowerCase().includes(query) || user.email.toLowerCase().includes(query))
    : users;
  const regularUsers = filteredUsers.filter(user => user.type === 'USER');
  const serviceAccounts = filteredUsers.filter(user => user.type === 'SERVICE_ACCOUNT');

  if (loading) {
    return <LoadingScreen message="Loading users..." />;
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 2 }}>
          <Alert severity="error">
            Error loading users: {error}
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PeopleIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: 'primary.main' }} />
            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              User Management
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            size="small"
          >
            Add User
          </Button>
        </Box>

        {/* Search */}
        <TextField
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ mb: 3, width: { xs: '100%', sm: 300 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* Regular Users Table */}
        <Typography variant="h5" sx={{ mb: 2 }}>
          Users
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Roles</TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>API Access</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {regularUsers.map((user) => (
                <TableRow
                  key={user.uuid}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/admin/users/${user.uuid}`)}
                >
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {user.roles.map((role) => (
                      <Chip
                        key={role}
                        label={role}
                        color={role === 'SUPER_ADMIN' ? 'error' : role === 'ORG_ADMIN' ? 'warning' : 'primary'}
                        variant="outlined"
                        size="small"
                        sx={{ mr: 0.5 }}
                      />
                    ))}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    <Chip
                      label={user.api_access_enabled ? 'Enabled' : 'Disabled'}
                      color={user.api_access_enabled ? 'success' : 'default'}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatDate(user.created_at)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); navigate(`/admin/users/${user.uuid}`); }}
                      title="Manage user"
                    >
                      <ManageIcon />
                    </IconButton>
                    {isAdmin() && !user.roles.includes('SUPER_ADMIN') && userUuid !== user.uuid && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(user); setDeleteDialogOpen(true); }}
                        aria-label="Delete user"
                        title="Delete user"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Service Accounts Table */}
        {serviceAccounts.length > 0 && (
          <>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Service Accounts
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Type</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Roles</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>API Access</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {serviceAccounts.map((user) => (
                    <TableRow key={user.uuid}>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Chip
                          label="Service Account"
                          variant="outlined"
                          size="small"
                          color="secondary"
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {user.roles.map((role) => (
                          <Chip
                            key={role}
                            label={role}
                            color={role === 'SUPER_ADMIN' ? 'error' : role === 'ORG_ADMIN' ? 'warning' : 'primary'}
                            variant="outlined"
                            size="small"
                            sx={{ mr: 0.5 }}
                          />
                        ))}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        <Chip
                          label={user.api_access_enabled ? 'Enabled' : 'Disabled'}
                          color={user.api_access_enabled ? 'success' : 'default'}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatDate(user.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {users.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No users found
            </Typography>
          </Box>
        )}

        {/* Dialogs */}
        <UserCreateDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onUserCreated={loadUsers}
        />

        {deleteTarget && (
          <ConfirmDialog
            open={deleteDialogOpen}
            title="Delete User"
            message={`Are you sure you want to delete "${deleteTarget.name}" (${deleteTarget.email})? This action cannot be undone. The user will be removed from the system and their account will be deleted.`}
            confirmText="Delete User"
            confirmColor="error"
            onConfirm={handleDeleteUser}
            onCancel={() => { setDeleteDialogOpen(false); setDeleteTarget(null); }}
          />
        )}
      </Box>
    </Container>
  );
}